
import { computed, defineComponent, onMounted } from "vue";
import { setCurrentPageTitle } from "@/core/helpers/breadcrumb";
import ReportSupervisorData from "@/data/payment";
import ApiService from "@/core/services/ApiService";
import { ElNotification } from "element-plus";
import { saveFile } from "@/core/services/FileSaverService";
import { saveAs } from "file-saver";
import { useStore } from "vuex";
import moment from "moment";

interface StatusOption {
  value: string;
  text: string;
}

interface EmployeeOption {
  value: string;
  text: string;
}

export default defineComponent({
  name: "ReportSupervisor",
  setup() {
    const store = useStore();
    onMounted(async () => {
      setCurrentPageTitle("Хянан шалгагч /тайлангууд/");
    });
    const isu = computed(() => {
      return store.getters.getMbUser;
    });
    const roles = computed(() => store.getters.getRoleNames || []);
    const canDownloadUpdateRequestReport = computed(() => {
      return (
        roles.value.includes("ROLE_UPDATE_REQUEST_REPORT") ||
        roles.value.includes("ROLE_ADMIN")
      );
    });
    return {
      isu,
      roles,
      canDownloadUpdateRequestReport
    };
  },
  data() {
    const currentYear = new Date().getFullYear();
    return {
      loadButton: "off",
      updateRequestDownloading: false,
      updateRequestFiltersLoading: false,
      updateRequestYear: String(currentYear),
      updateRequestCreatedBy: "",
      updateRequestStatus: "",
      updateRequestYears: Array.from({ length: 10 }, (_, index) =>
        String(currentYear - index)
      ),
      updateRequestCreatedByOptions: [] as EmployeeOption[],
      updateRequestStatuses: [
        { value: "PENDING", text: "Хүлээгдэж буй" },
        { value: "CHECKED", text: "Хянагдсан" },
        { value: "APPROVED", text: "Батлагдсан" },
        { value: "REJECTED", text: "Татгалзсан" }
      ] as StatusOption[],
      historyText: "",
      historyReportType: "",
      historyUsername: "",
      historyOrgcode: "",
      historyDateRange: null as string[] | null,
      historyPage: 1,
      historyPageSize: 10,
      historyTotal: 0,
      registerno: "",
      monthValue:
        (new Date().getMonth() + 1).toString().length === 2
          ? (new Date().getMonth() + 1).toString()
          : "0" + (new Date().getMonth() + 1).toString(),
      months: ReportSupervisorData[0]["months"],
      yearValue: "2022",
      orgList: [],
      reportLog: [],
      reportLogLoading: true,
      orgValue: "",
      dateOfStart: "",
      dateOfEnd: "",
      years: [
        {
          yearValue: "2022",
          yearLabel: "2022"
        },
        {
          yearValue: "2021",
          yearLabel: "2021"
        }
      ]
    };
  },
  mounted() {
    if (!this.isu) {
      ElNotification({
        title: "Анхааруулга",
        message: "Хандах эрхгүй байна.",
        type: "warning",
        iconClass: "el-text-warning"
      });
    } else {
      this.getRequiredData();
    }
  },
  methods: {
    async getRequiredData() {
      this.reportLogLoading = false;
      const requests: Array<Promise<void>> = [this.getReportHistory()];
      if (this.canDownloadUpdateRequestReport) {
        requests.push(this.getUpdateRequestReportFilters());
      }
      await Promise.all(requests);
    },
    async getUpdateRequestReportFilters() {
      this.updateRequestFiltersLoading = true;
      await ApiService.get("update-request-report/filters")
        .then(res => {
          const results = res.data;
          if (results.code === 401) {
            ElNotification({
              title: "Анхааруулга",
              message: results.warning,
              type: "warning",
              iconClass: "el-text-warning"
            });
          } else if (results.code === 0) {
            ElNotification({
              title: "Алдаа",
              message: results.error,
              type: "error",
              iconClass: "el-text-error"
            });
          } else {
            const response = results.response || {};
            const years: string[] = (response.years || []).map(year =>
              String(year)
            );
            this.updateRequestYears = Array.from(
              new Set<string>(years.concat(this.updateRequestYears))
            ).sort((left, right) => Number(right) - Number(left));
            this.updateRequestCreatedByOptions = response.createdBy || [];
            if (response.statuses?.length) {
              this.updateRequestStatuses = response.statuses;
            }
          }
        })
        .catch(() => {
          ElNotification({
            title: "Алдаа",
            message:
              "Хүсэлт шийдвэрлэлтийн тайлангийн шүүлтүүр уншихад алдаа гарлаа.",
            type: "error",
            iconClass: "el-text-error"
          });
        })
        .finally(() => {
          this.updateRequestFiltersLoading = false;
        });
    },
    async downloadUpdateRequestReport() {
      if (!this.updateRequestYear) {
        ElNotification({
          title: "Анхааруулга",
          message: "Тайлангийн он сонгоно уу.",
          type: "warning"
        });
        return;
      }

      this.updateRequestDownloading = true;
      document.body.classList.add("waiting");
      const params = {
        year: this.updateRequestYear,
        createdBy: this.updateRequestCreatedBy || null,
        status: this.updateRequestStatus || null
      };

      ApiService.setHeader();
      await ApiService.vueInstance.axios
        .post("update-request-report", {}, { params, responseType: "blob" })
        .then(response => {
          const contentType = response.headers["content-type"] || "";
          if (contentType.includes("application/json")) {
            this.handleUpdateRequestBlobError(response.data);
            return;
          }
          const creator = this.updateRequestCreatedBy
            ? `_${this.safeFilePart(this.updateRequestCreatedBy)}`
            : "";
          const status = this.updateRequestStatus
            ? `_${this.updateRequestStatus.toLowerCase()}`
            : "";
          saveAs(
            response.data,
            `update_request_report_${this.updateRequestYear}${creator}${status}.xlsx`
          );
          this.getReportHistory();
        })
        .catch(() => {
          ElNotification({
            title: "Алдаа",
            message: "Хүсэлт шийдвэрлэлтийн тайлан татахад алдаа гарлаа.",
            type: "error"
          });
        })
        .finally(() => {
          document.body.classList.remove("waiting");
          this.updateRequestDownloading = false;
        });
    },
    handleUpdateRequestBlobError(blob: Blob) {
      const reader = new FileReader();
      reader.onload = () => {
        let message = "Хүсэлт шийдвэрлэлтийн тайлан татахад алдаа гарлаа.";
        try {
          const result = JSON.parse(String(reader.result || "{}"));
          message = result.error || result.warning || message;
        } catch (error) {
          message = String(reader.result || message);
        }
        ElNotification({ title: "Алдаа", message, type: "error" });
      };
      reader.readAsText(blob);
    },
    safeFilePart(value: string): string {
      return value.replace(/[^a-zA-Z0-9_-]/g, "_");
    },
    async getReportHistory() {
      this.reportLogLoading = true;
      const params = {
        page: this.historyPage,
        pageSize: this.historyPageSize,
        text: this.historyText || null,
        reportType: this.historyReportType || null,
        username: this.historyUsername || null,
        orgcode: this.historyOrgcode || null,
        dateStart: this.historyDateRange?.[0] || null,
        dateEnd: this.historyDateRange?.[1] || null
      };
      await ApiService.query("report-history", { params })
        .then(response => {
          const result = response.data;
          if (result.code === 401) {
            ElNotification({
              title: "Анхааруулга",
              message: result.warning,
              type: "warning"
            });
          } else if (result.code === 0) {
            ElNotification({
              title: "Алдаа",
              message: result.error,
              type: "error"
            });
          } else {
            this.reportLog = result.response.rows || [];
            this.historyTotal = Number(result.response.total || 0);
          }
        })
        .catch(() => {
          ElNotification({
            title: "Алдаа",
            message: "Тайлан таталтын түүх уншихад алдаа гарлаа.",
            type: "error"
          });
        })
        .finally(() => {
          this.reportLogLoading = false;
        });
    },
    applyHistoryFilters() {
      this.historyPage = 1;
      this.getReportHistory();
    },
    resetHistoryFilters() {
      this.historyText = "";
      this.historyReportType = "";
      this.historyUsername = "";
      this.historyOrgcode = "";
      this.historyDateRange = null;
      this.historyPage = 1;
      this.getReportHistory();
    },
    handleHistoryPageChange(page: number) {
      this.historyPage = page;
      this.getReportHistory();
    },
    handleHistoryPageSizeChange(pageSize: number) {
      this.historyPageSize = pageSize;
      this.historyPage = 1;
      this.getReportHistory();
    },
    async getReportTotalLoans() {
      this.loadButton = "on";
      await ApiService.get("reportindex", "?type=total_loans")
        .then(res => {
          const results = res.data;
          if (results.code === 401) {
            ElNotification({
              title: "Анхааруулга",
              message: results.warning,
              type: "warning",
              iconClass: "el-text-warning"
            });
          } else if (results.code === 0) {
            ElNotification({
              title: "Алдаа",
              message: results.error,
              type: "error",
              iconClass: "el-text-error"
            });
          } else {
            saveFile(
              results.response,
              "LoanBalanceByOrg",
              "application/vnd.ms-excel",
              ".xls"
            );
          }
        })
        .catch(() => {
          ElNotification({
            title: "Алдаа",
            message: "Тайлан татахад алдаа гарлаа.",
            type: "error",
            iconClass: "el-text-error"
          });
        })
        .finally(() => {
          this.loadButton = "off";
        });
    },
    async getReportInvoiceReport() {
      this.loadButton = "on";
      const params = {
        type: "invoice_report",
        year: this.yearValue,
        month: this.monthValue
      };
      await ApiService.post("reportindex", { params })
        .then(res => {
          const results = res.data;
          if (results.code === 401) {
            ElNotification({
              title: "Анхааруулга",
              message: results.warning,
              type: "warning",
              iconClass: "el-text-warning"
            });
          } else if (results.code === 0) {
            ElNotification({
              title: "Алдаа",
              message: results.error,
              type: "error",
              iconClass: "el-text-error"
            });
          } else {
            saveFile(
              results.response,
              "InvoiceReport_" + this.yearValue + this.monthValue,
              "application/vnd.ms-excel",
              ".xls"
            );
          }
        })
        .catch(() => {
          ElNotification({
            title: "Алдаа",
            message: "Тайлан татахад алдаа гарлаа.",
            type: "error",
            iconClass: "el-text-error"
          });
        })
        .finally(() => {
          this.loadButton = "off";
        });
    },
    async getReportInvoicePayment() {
      if (
        this.orgValue == "" ||
        this.dateOfStart == "" ||
        this.dateOfEnd == ""
      ) {
        ElNotification({
          title: "Анхааруулга",
          message: "Аль нэг утга хоосон байна.",
          type: "warning",
          iconClass: "el-text-warning"
        });
        return;
      }
      this.loadButton = "on";

      const fromdate = moment(this.dateOfStart).format("YYYY-MM-DD");
      const todate = moment(this.dateOfEnd).format("YYYY-MM-DD");
      const params = {
        type: "SupplyLoanList",
        orgcode: this.orgValue,
        fromdate: fromdate,
        todate: todate
      };
      await ApiService.post("reportindex", { params })
        .then(res => {
          const results = res.data;
          if (results.code === 401) {
            ElNotification({
              title: "Анхааруулга",
              message: results.warning,
              type: "warning",
              iconClass: "el-text-warning"
            });
          } else if (results.code === 0) {
            ElNotification({
              title: "Алдаа",
              message: results.error,
              type: "error",
              iconClass: "el-text-error"
            });
          } else {
            saveFile(
              results.response,
              "supplyLoanList_" + this.orgValue,
              "application/vnd.ms-excel",
              ".xls"
            );
          }
        })
        .catch(() => {
          ElNotification({
            title: "Алдаа",
            message: "Тайлан татахад алдаа гарлаа.",
            type: "error",
            iconClass: "el-text-error"
          });
        })
        .finally(() => {
          this.loadButton = "off";
        });
    },
    async getReportReportInquiry() {
      if (this.registerno === "") {
        ElNotification({
          title: "Анхааруулга",
          message: "Хоосон байна.",
          type: "warning",
          iconClass: "el-text-warning"
        });
        return;
      }
      this.loadButton = "on";
      const params = {
        type: "reportInquiry",
        registerno: this.registerno
      };
      await ApiService.post("reportindex", { params })
        .then(res => {
          const results = res.data;
          if (results.code === 401) {
            ElNotification({
              title: "Анхааруулга",
              message: results.warning,
              type: "warning",
              iconClass: "el-text-warning"
            });
          } else if (results.code === 0) {
            ElNotification({
              title: "Алдаа",
              message: results.error,
              type: "error",
              iconClass: "el-text-error"
            });
          } else {
            saveFile(
              results.response,
              "InvoiceReport_" + this.registerno,
              "application/vnd.ms-excel",
              ".xls"
            );
          }
        })
        .catch(() => {
          ElNotification({
            title: "Алдаа",
            message: "Тайлан татахад алдаа гарлаа.",
            type: "error",
            iconClass: "el-text-error"
          });
        })
        .finally(() => {
          this.loadButton = "off";
        });
    }
  }
});
